import React, { FC } from 'react';
import { makeStyles, Table, TableBody, TableCell, TableHead, TableRow, Theme, Typography } from '@material-ui/core';
import { format } from 'date-fns';

interface Props {
  changes: AppLogChanges | AppLogTruncatedChanges;
}

interface ChangeRow {
  field: string;
  from: any;
  to: any;
}

const ISO_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const SUMMARY_FIELDS = ['name', 'title', 'serviceTitle', 'description', 'invoiceNumber', 'label'];

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    '& th, & td': {
      verticalAlign: 'top',
      padding: theme.spacing(1, 2)
    }
  },
  field: {
    width: '30%',
    fontWeight: 500
  },
  value: {
    width: '35%',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },
  from: {
    color: theme.palette.error.dark
  },
  to: {
    color: theme.palette.success.dark
  },
  empty: {
    color: theme.palette.text.disabled,
    fontStyle: 'italic'
  }
}));

const isTruncated = (changes: AppLogChanges | AppLogTruncatedChanges): changes is AppLogTruncatedChanges =>
  (changes as AppLogTruncatedChanges).truncated === true && Array.isArray((changes as AppLogTruncatedChanges).fields);

const isFieldChange = (change: AppLogFieldChange | AppLogArrayChange): change is AppLogFieldChange => 'from' in change || 'to' in change;

/** "ServiceAddress.postalCode" -> "Service Address › Postal Code" */
const humanizeField = (field: string): string =>
  field
    .split('.')
    .map(part =>
      part
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .replace(/^./, char => char.toUpperCase())
    )
    .join(' › ');

/** Short text for an added / removed record, e.g. "Aircon Servicing (#12)" */
const summarizeRecord = (record: any): any => {
  if (record === null || typeof record !== 'object' || Array.isArray(record)) {
    return record;
  }

  const summaryField = SUMMARY_FIELDS.find(field => typeof record[field] === 'string' && record[field]);

  if (!summaryField) {
    return record;
  }

  return record.id ? `${record[summaryField]} (#${record.id})` : record[summaryField];
};

const toRows = (changes: AppLogChanges, prefix = ''): ChangeRow[] =>
  Object.keys(changes).reduce<ChangeRow[]>((rows, key) => {
    const change = changes[key];
    const field = prefix ? `${prefix}.${key}` : key;

    if (isFieldChange(change)) {
      return [...rows, { field, from: change.from, to: change.to }];
    }

    const { added = [], removed = [], changed = {} } = change;

    return [
      ...rows,
      ...added.map(item => ({ field: `${field} (added)`, from: null, to: summarizeRecord(item) })),
      ...removed.map(item => ({ field: `${field} (removed)`, from: summarizeRecord(item), to: null })),
      ...Object.keys(changed).reduce<ChangeRow[]>((changedRows, id) => [...changedRows, ...toRows(changed[id], `${field}.#${id}`)], [])
    ];
  }, []);

const ChangesTable: FC<Props> = props => {
  const classes = useStyles();
  const { changes } = props;

  const renderValue = (value: any) => {
    if (value === null || value === undefined || value === '') {
      return <span className={classes.empty}>empty</span>;
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'string' && ISO_DATE_TIME_PATTERN.test(value)) {
      const date = new Date(value);

      return isNaN(date.getTime()) ? value : format(date, 'dd-MM-yyyy hh:mm a');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  };

  if (isTruncated(changes)) {
    return (
      <Typography variant='body2' color='textSecondary'>
        The change was too large to keep in full. Changed fields: {changes.fields.map(humanizeField).join(', ')}
      </Typography>
    );
  }

  const rows = toRows(changes);

  if (rows.length === 0) {
    return (
      <Typography variant='body2' color='textSecondary'>
        No field was changed
      </Typography>
    );
  }

  return (
    <Table size='small' className={classes.table}>
      <TableHead>
        <TableRow>
          <TableCell className={classes.field}>Field</TableCell>
          <TableCell className={classes.value}>Before</TableCell>
          <TableCell className={classes.value}>After</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow key={`${row.field}-${index}`}>
            <TableCell className={classes.field}>{humanizeField(row.field)}</TableCell>
            <TableCell className={`${classes.value} ${classes.from}`}>{renderValue(row.from)}</TableCell>
            <TableCell className={`${classes.value} ${classes.to}`}>{renderValue(row.to)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default ChangesTable;
